import { useMemo, type ReactNode } from 'react';
import { toast as baseToast } from 'sonner';
import { HugeiconsIcon, type IconSvgElement as HugeIconDefinition } from '@hugeicons/react';
import {
  Alert02Icon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
  Notification01Icon,
} from '@hugeicons/core-free-icons';

type ToastVariant = 'default' | 'success' | 'warning' | 'destructive';

export type ToastOptions = {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  duration?: number;
  variant?: ToastVariant;
  icon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
};

const renderToastIcon = (icon: HugeIconDefinition) => (
  <HugeiconsIcon
    aria-hidden="true"
    color="currentColor"
    focusable="false"
    icon={icon}
    size={16}
    strokeWidth={1.9}
  />
);

const VARIANT_MAP: Record<
  ToastVariant,
  { icon: ReactNode; badgeClass: string; accentClass: string }
> = {
  default: {
    icon: renderToastIcon(Notification01Icon),
    badgeClass: 'bg-white/15 text-white',
    accentClass: 'from-slate-900/90 via-slate-950/80 to-slate-900/70',
  },
  success: {
    icon: renderToastIcon(CheckmarkCircle02Icon),
    badgeClass: 'bg-emerald-400/20 text-emerald-200',
    accentClass: 'from-emerald-900/90 via-emerald-950/80 to-emerald-900/70',
  },
  warning: {
    icon: renderToastIcon(Alert02Icon),
    badgeClass: 'bg-amber-400/20 text-amber-100',
    accentClass: 'from-amber-900/90 via-amber-950/80 to-amber-900/70',
  },
  destructive: {
    icon: renderToastIcon(Alert02Icon),
    badgeClass: 'bg-rose-500/20 text-rose-100',
    accentClass: 'from-rose-900/90 via-rose-950/80 to-rose-900/70',
  },
};

const buildToast = (options: ToastOptions) => {
  const {
    id,
    title,
    description,
    variant = 'default',
    icon,
    duration,
    actionLabel,
    onAction,
  } = options;
  const tone = VARIANT_MAP[variant] ?? VARIANT_MAP.default;
  const finalDuration = duration ?? (variant === 'destructive' ? 4200 : 2800);

  return baseToast.custom(
    (toastId) => (
      <div
        className={`pointer-events-auto flex w-[min(420px,calc(100vw-32px))] items-start gap-3 rounded-3xl border border-white/12 bg-gradient-to-br ${tone.accentClass} p-4 text-white shadow-2xl shadow-black/40 backdrop-frost-2xl`}
      >
        <div
          className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-2xl ${tone.badgeClass}`}
        >
          {icon ?? tone.icon ?? renderToastIcon(InformationCircleIcon)}
        </div>
        <div className="flex flex-1 flex-col">
          {title && <p className="text-body font-semibold leading-tight">{title}</p>}
          {description && (
            <p className="mt-1 text-meta leading-relaxed text-white/70">{description}</p>
          )}
        </div>
        {actionLabel && onAction && (
          <button
            onClick={() => {
              onAction();
              baseToast.dismiss(toastId);
            }}
            type="button"
            className="min-h-11 min-w-11 shrink-0 rounded-full border border-white/25 px-3 py-2 text-meta font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {actionLabel}
          </button>
        )}
      </div>
    ),
    {
      id,
      duration: finalDuration,
      dismissible: true,
      className: 'bg-transparent shadow-none p-0',
    },
  );
};

const toast = (options: ToastOptions) => buildToast(options);

const useToast = () => {
  return useMemo(
    () => ({
      toast,
      dismiss: (toastId?: string) => baseToast.dismiss(toastId),
    }),
    [],
  );
};

export { useToast, toast };
